import { Component, OnInit, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from 'app/layouts/main';
import { Log } from 'app/shared/logs/types';

@Component({
    selector: 'mh-log-card',
    templateUrl: './log-card.component.html',
    styleUrls: ['./log-card.component.scss'],
    standalone: false
})
export class LogCardComponent implements OnInit {
  @Input() log: Log;
  @Input() language: string;
  @Input() commercialExecutivesList = [];
  @Input() reasons_discarded;
  @Input() currentLangId;
  @Input() deleteReasonsList;

  userManage: string;
  dateManage: string;
  surveyIdManage: string | number;
  isReviewResource = false;

  EXCLUDED_FIELDS = ['updated_at', 'id', 'deleted', 'created_at', 'lastActivity', 'typeid', ',item_id', 'product_id', 'status_id'];

  constructor(private translate: TranslateService, private utilService: UtilService) {}

  ngOnInit() {
    this.isReviewResource = (this.log?.resourceName || this.log?.resource_name) === 'review';
    this.getCommercialExecutiveNames();
    this.setDataManaged();

    if (this.isSurveyDeleted || this.isSurveyReactivated) {
      const npsValue = this.log?.log_content?.nps;
      this.log.log_content.nps = npsValue !== undefined && npsValue !== null ? 
        this.translate.instant('qualifys.' + this.utilService.qualifyOnlineNps(npsValue)) : 
        '-';
    }
  }

  getValueOrDash(value: any) {return value === false ? false : value || '-' }

  getTranslateParam(change: any) {
    if (change?.name === 'flag_answered') {
      return {
        actual: this.getReviewStatusLabel(change.actual),
        previous: this.getReviewStatusLabel(change.previous),
      };
    }

    return {
      actual: this.getValueOrDash(this.getAreasValue(change, 'actual') || this.getAreasValue(change, 'tkpi')),
      previous: this.getValueOrDash(this.getAreasValue(change, 'previous')|| this.getAreasValue(change, 'tkpi_previous'))
    }
  }

  getReviewStatusLabel(status: string) {
    const key = `logs.resources.review.status.${status}`;
    const label = this.translate.instant(key);
    return label === key ? this.getValueOrDash(status) : label;
  }

  // el for Translation hace referencia al nombre del cambio
  getTranslateParamForTranslation(change: any) {
    return {
      actual: this.getValueOrDash(change.actual && change.actual.fieldValue),
      previous: this.getValueOrDash(change.previous && change.previous.fieldValue),
    }
  }

  getAreasValue(change, type) {
    if (change && !change.name) return '-';

    if (type === 'actual' && !['related_area', 'polarity'].includes(change.name)) return change.actual;
    if (type === 'previous' && !['related_area', 'polarity'].includes(change.name)) return change.previous;
    if (type === 'tkpi' && change.name !== 'related_area') return change.tkpi_value_dto?.actual;
    if (type === 'tkpi_previous' && change.name !== 'related_area') return change.tkpi_value_dto?.previous;

    if (type === 'actual' && change.name === 'related_area') {
      return (this.language === 'es') ?
        change.actual?.area_entity.clasification_text || '-':
        change.actual?.area_entity.clasification_text_en || '-';
    }

    if (type === 'previous' && change.name === 'related_area') {
      return (this.language === 'es') ?
        change.previous?.area_entity.clasification_text || '-':
        change.previous?.area_entity.clasification_text_en || '-';
    }

    if (change.name === 'polarity') {
      return { name: this.translate.instant('shared.semantic.type_comment.' + change[type]?.value) || '' };
    }

    if (type === 'tkpi' && change.name === 'related_area') {
      return (this.language === 'es') ?
        change.tkpi_value_dto?.actual?.area_entity.clasification_text || '-':
        change.tkpi_value_dto?.actual?.area_entity.clasification_text_en || '-';
    }

    if (type === 'tkpi_previous' && change.name === 'related_area') {
      return (this.language === 'es') ?
        change.tkpi_value_dto?.previous?.area_entity.clasification_text || '-':
        change.tkpi_value_dto?.previous?.area_entity.clasification_text_en || '-';
    }

  }

  getCommercialExecutiveNames() {
    if (this.commercialExecutivesList.length && this.log.changes.length) {
      this.log.changes.map((change) => {
        if (change.name === "commercial_executive") {
          const prevExecutive = this.commercialExecutivesList.find((executive) => change.previous === executive.id);
          change.previous = prevExecutive ? `${prevExecutive['first_name']}  ${prevExecutive['last_name']}` : change.previous;

          const currExecutive = this.commercialExecutivesList.find((executive) => change.actual === executive.id);
          change.actual = currExecutive ? `${currExecutive['first_name']}  ${currExecutive['last_name']}` : change.actual;
        }
      });
    }
  }

  setDataManaged() {
    this.userManage = this.log.user.complete_name;
    this.surveyIdManage = this.log.resourceId;
    if (this.log?.resourceName === 'answered_survey') {
      this.dateManage = this.log.changes[0].action_date;
    }
  }

  get isReportLog() {
    return this.log?.category === 'reports';
  }

  get isReviewLog() {
    return this.log?.resourceName === 'review_reply';
  }

  get isReviewManaged() {
    return this.log?.resourceName === 'answered_survey';
  }

  get isSentiment() {
    return this.log?.resourceName === 'modify_semantic_comments';
  }

  get isSurveyAnsweredDeleted() {
    return this.log?.resourceName === 'surveys';
  }

  get isReprocessSurvey() {
    return this.log?.resourceName === 'recalculate_stats';
  }

  get isSurveyDeleted() {
    return this.log?.resourceName === 'delete_surveys';
  }

  get isSurveyReactivated() {
    return this.log?.resourceName === 'reactivate_surveys';
  }

  get isAutomaticAlert() {
    return this.log?.resourceName === 'automatic-alert';
  }

  get ReasonText() {
    const reason = this.reasons_discarded?.find( reason => reason?.id === this.log?.log_content?.proposalCaseDiscardedDto?.reasonId);
    return reason?.translates.find( reasonLang => reasonLang.language_id === this.currentLangId)?.name || '';
  }

  get deleteReasonText() {
    const reason = this.deleteReasonsList?.filter(
      (reason) => reason?.reason_id === this.log?.log_content?.deletionReason?.reasonId,
    );
    return reason?.find((reasonLang) => reasonLang.language_id === this.currentLangId)?.reason || '';
  }

  get isContractLog() {
    return this.log?.resourceName === 'customer_contracts';
  }

  get isContractDownload() {
    return this.isContractLog && this.log?.requestMethodName === 'downloadConctractAuditTrigger';
  }

  get isContractDelete() {
    return this.isContractLog && this.log?.requestMethodName === 'deleteCustomerFileAuditTrigger';
  }

  get isContractUpload() {
    return this.isContractLog && this.log?.requestMethodName === 'save';
  }

  get isMarkKnowledgeFound() {
    return this.log?.resourceName === 'update_chat_message_knowledge_found';
  }

  get isDeactivateInactiveUsers() {
    return (
      (this.log?.request_method_name?.toLowerCase() || this.log?.requestMethodName?.toLowerCase()) ===
      'deactivateinactiveusers'
    );
  }

  get userTargetName() {
    const target = this.log?.userTarget;
    if (!target) return '';
    return [target.firstName || target.first_name, target.lastName || target.last_name].filter(Boolean).join(' ');
  }

  get userTargetLabel() {
    const email = this.log?.userTarget?.email;
    if (!this.userTargetName) return '';
    return email ? `${this.userTargetName} (${email})` : this.userTargetName;
  }
}
